import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Typography,
    useMediaQuery,
    useTheme, Grow
} from "@mui/material";
import {
    useGetProfileQuery,
} from "../features/api/userApiSlice";
import {
    useGetGoldenEggDescQuery,
    useClaimGoldenEggMutation,
    useGetGoldenEggCostQuery
} from "../features/api/gameApiSlice";
import { useNavigate } from "react-router-dom";
import * as Constant from "../features/constant";
import { useTranslation } from "react-i18next";

export default function GoldenEgg() {
    const theme = useTheme();
    const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [eggs] = useState(
        Array(9).fill({ cracked: false, revealed: false })
    );
    const [selectedReward, setSelectedReward] = useState(null);
    const [currentPoints, setCurrentPoints] = useState(0);
    const [currentCredit, setCurrentCredit] = useState(0);
    const [open, setOpen] = useState(false);
    const [rotate, setRotate] = useState(false);
    const [reward, setReward] = useState(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const smashSoundRef = useRef(null);
    const eggCrackSoundRef = useRef(null);
    const [isClaiming, setIsClaiming] = useState(false);
    const [rewards, setRewards] = useState([]);

    const eggImages = [
        "/assets/img/golden_egg/egg/Bronze.png",
        "/assets/img/golden_egg/egg/Silver.png",
        "/assets/img/golden_egg/egg/Gold.png",
    ];

    const eggGif = [
        "/assets/img/golden_egg/egg/Bronze.gif",
        "/assets/img/golden_egg/egg/Silver.gif",
        "/assets/img/golden_egg/egg/Gold.gif",
    ];

    const hammerImages = [
        { src: "/assets/img/golden_egg/hammer/bronze.png", alt: "Bronze Hammer" },
        { src: "/assets/img/golden_egg/hammer/silver.png", alt: "Silver Hammer" },
        { src: "/assets/img/golden_egg/hammer/gold.png", alt: "Gold Hammer" },
    ];

    const {
        data: user,
        isLoading: isUserLoading,
        isError: isUserError,
        isSuccess: isUserSuccess,
        error: userError,
        refetch,
    } = useGetProfileQuery();

    const {
        data: goldenEggDesc,
        isLoading: isgoldenEggDescLoading,
        isError: isgoldenEggDescError,
        isSuccess: isgoldenEggDescSuccess,
        error: goldenEggDescError,
    } = useGetGoldenEggDescQuery();

    const {
        data: goldenEggCost,
        isLoading: isgoldenEggCostLoading,
        isError: isgoldenEggCostError,
        isSuccess: isgoldenEggCostSuccess,
        error: goldenEggCostError,
    } = useGetGoldenEggCostQuery();


    const [claimGoldenEgg, { isLoading }] = useClaimGoldenEggMutation();
    const initialized = useRef(false);

    useEffect(() => {
        if (isUserSuccess && user?.result?.activityPoint != null) {
            setCurrentPoints(user.result.activityPoint);
            setCurrentCredit(user.result.currentCredit);
        }

        if (!initialized.current && isUserSuccess && user?.result?.activityPoint != null) {
            initialized.current = true;
            if (user.result.activityPoint >= 100) {
                setSelectedReward(0);
            }
        }
    }, [isUserSuccess, user]);

    // Track mouse movement
    useEffect(() => {
        const handleMove = (e) => setPos({ x: e.clientX, y: e.clientY });
        window.addEventListener("mousemove", handleMove);
        return () => window.removeEventListener("mousemove", handleMove);
    }, []);

    useEffect(() => {
        const swing = new Audio(`${process.env.PUBLIC_URL}/assets/img/golden_egg/sound_effect/swing.mp3`);
        const egg = new Audio(`${process.env.PUBLIC_URL}/assets/img/golden_egg/sound_effect/egg_crack.mp3`);

        swing.preload = "auto";
        swing.load();

        egg.preload = "auto";
        egg.load();

        smashSoundRef.current = swing;
        eggCrackSoundRef.current = egg;
    }, []);

    useEffect(() => {
        if (!goldenEggCost?.result) return;

        let parsedResult = goldenEggCost.result;

        try {
            // parse if it's a JSON string
            if (typeof parsedResult === "string") {
                parsedResult = JSON.parse(parsedResult);
            }
        } catch (err) {
            console.error("Failed to parse goldenEggCost.result:", err);
            parsedResult = {};
        }

        let rewardList = [];

        if (Object.keys(parsedResult).length === 0) {
            rewardList = [
                { points: 100, label: "100", rank: "bronze" },
                { points: 200, label: "200", rank: "silver" },
                { points: 300, label: "300", rank: "gold" },
            ];
        } else {
            rewardList = Object.entries(parsedResult).map(([key, value]) => ({
                points: Number(value),
                label: value,
                rank: key,
            }));
        }

        setRewards(rewardList);
    }, [goldenEggCost]);


    // Click (hammer smash)
    const handleClick = () => {
        const sound = smashSoundRef.current;
        if (sound) {
            sound.currentTime = 0;
            sound.play().catch(() => { });
        }

        setRotate(true);
        setTimeout(() => setRotate(false), 200);
    };

    const crackEgg = async () => {
        const point = rewards[selectedReward].points;
        const rank = rewards[selectedReward].rank;
        setIsClaiming(true);

        try {
            const res = await claimGoldenEgg({ point, rank }).unwrap();

            const sound = eggCrackSoundRef.current;
            if (sound) {
                sound.currentTime = 0;
                await sound.play().catch(() => { });
            }

            setReward(res?.result);

            setTimeout(() => setOpen(true), 300);
        } catch (err) {
            console.error("Claim failed:", err);
        } finally {
            await refetch();
        }
    };

    const selectReward = (index) => {
        const cost = rewards[index].points;
        if (currentPoints >= cost) {
            setSelectedReward(index);
        }
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                position: "relative",
                height: "100vh",
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "none",
                WebkitOverflowScrolling: "touch",
                p: { xs: 2, sm: 4 },
                cursor: selectedReward !== null ? "none" : "default",
            }}
        >
            <Box
                sx={{
                    position: "fixed",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage:
                        "linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/assets/img/golden_egg/background/bg.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    zIndex: 0,
                }}
            />
            {selectedReward !== null && !mobileView && (
                <Box
                    component="img"
                    src={hammerImages[selectedReward]?.src}
                    alt="Hammer cursor"
                    sx={{
                        position: "fixed",
                        top: pos.y,
                        left: pos.x,
                        width: 60,
                        height: 60,
                        pointerEvents: "none",
                        transform: `${rotate ? "rotate(-40deg)" : "rotate(0deg)"} translate(-40%, -40%)`,
                        transformOrigin: "top right",
                        transition: "transform 0.15s ease",
                        zIndex: 9999,
                    }}
                />
            )}
            <Box sx={{ position: "relative", zIndex: 1, color: "white" }}>
                {/* Popup Dialog */}
                <Dialog
                    open={open}
                    disableEscapeKeyDown
                    hideBackdrop={false}
                    TransitionComponent={Grow}
                    TransitionProps={{
                        timeout: 0, // instant, no visible animation
                        onEntered: () => {
                            // ✅ this runs once the Dialog has fully opened
                            setIsClaiming(false);
                        },
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: 4,
                            backgroundColor: "rgba(0, 0, 0, 0.8)",
                            color: "white",
                            p: 2,
                            textAlign: "center",
                            border: `2px solid ${theme.palette.primary.main}`,
                            cursor: "none",
                        },
                    }}

                    onClose={(event, reason) => {
                        if (reason === "backdropClick") return; // ignore backdrop clicks
                        setOpen(false); // only close manually
                    }}

                >
                    <DialogContent>
                        {/* <video
                        autoPlay
                        muted
                        loop={false}
                        playsInline
                        src={eggGif[selectedReward ?? 0]}
                        onEnded={(e) => {
                            e.target.pause();
                            e.target.currentTime = e.target.duration; // Freeze on last frame
                        }}
                        style={{
                            height: 200,
                            width: "auto",
                            objectFit: "contain",
                            display: "block",
                            borderRadius: "8px",
                            justifySelf: "center"
                        }}
                    /> */}
                        <img
                            src={eggGif[selectedReward ?? 0]}
                            alt="Egg"
                            style={{
                                height: 200,
                                width: "auto",
                                objectFit: "contain",
                                display: "block",
                                borderRadius: "8px",
                                justifySelf: "center"
                            }}
                        />
                        <Typography style={{
                            color: theme.palette.primary.main,
                            marginTop: 15,
                            fontSize: mobileView ? 16 : 20
                        }}>
                            {(() => {
                                switch (reward?.rewardType) {
                                    case "Bonus":
                                        return t("goldenegg.earnedamount", { amount: reward?.amount, currency: process.env.REACT_APP_CURRENCY_CODE });
                                    case "Activity Point":
                                        return t("goldenegg.earnedpoint", { amount: reward?.amount });
                                    case "Link":
                                        return t("goldenegg.winreward", { reward: reward?.rewardName });
                                    case "Promotion ID":
                                        return t("goldenegg.winpromo", { reward: reward?.rewardName });
                                    case "No Reward":
                                        return t("goldenegg.noreward");
                                    default:
                                        return "Please Try Again";
                                }
                            })()}
                        </Typography>
                    </DialogContent>

                    <DialogActions sx={{ justifyContent: "center" }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => setOpen(false)}
                            sx={{ borderRadius: 3, textTransform: "none", width: "100%", cursor: "none" }}
                        >
                            {t("goldenegg.close")}
                        </Button>
                    </DialogActions>
                </Dialog >

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        p: mobileView ? 1 : 2,
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(Constant.getPath("/"))}
                        sx={{
                            px: mobileView ? 3 : 4,
                            fontSize: mobileView ? 12 : 16,
                            cursor: "none"
                        }}
                    >
                        {t("goldenegg.back")}
                    </Button>
                </Box>
                <Box maxWidth="lg" mx="auto">
                    <Box display={"flex"}>
                        <Box
                            sx={{
                                flex: 1,
                                position: "relative",
                                backgroundImage: `url(${process.env.REACT_APP_LOGO})`,
                                backgroundRepeat: "no-repeat",
                                backgroundSize: { xs: "100% 100%", sm: "90% 70%" },
                                backgroundPosition: "center",
                                display: mobileView ? "none" : undefined,
                                minHeight: { xs: 150, sm: 200, md: 300 },
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                navigate(Constant.getPath("/"));
                            }}
                        />

                        <Box
                            sx={{
                                flex: 1,
                                position: "relative",
                                backgroundImage: `url(/assets/img/golden_egg/title/title.png)`,
                                backgroundRepeat: "no-repeat",
                                backgroundSize: { xs: "100% 100%", sm: "100% 100%" },
                                backgroundPosition: "center",
                                minHeight: { xs: 150, sm: 200, md: 300 },
                            }}
                        />
                    </Box>

                    {/* Top Stats & Reward Selector */}
                    <Box
                        width="100%"
                        display="flex"
                        flexDirection={{ xs: "column", md: "row" }}
                        mb={2}
                        gap={!mobileView ? 2 : undefined}
                    >
                        <Box
                            sx={{
                                backgroundImage: `url(${process.env.REACT_APP_LOGO})`,
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "contain",
                                mt: -3,
                                backgroundPosition: "center",
                                width: { xs: 180, sm: 200 },
                                height: { xs: 100, sm: 130 },
                                alignSelf: "center",
                                display: !mobileView ? "none" : undefined
                            }}
                            onClick={() => {
                                navigate(Constant.getPath("/"));
                            }}
                        />

                        <Box
                            sx={{
                                flex: 1,
                                position: "relative",
                                backgroundImage: mobileView ? `url(/assets/img/golden_egg/board/board_mobile.png)` : `url(/assets/img/golden_egg/board/board_long.png)`,
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "100% 100%",
                                backgroundPosition: "center",
                                minHeight: { xs: 120, sm: 160, md: 180 },
                                display: "flex",
                                mt: mobileView ? -2 : undefined,
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                color: "white",
                                p: { xs: 2, sm: 3 },
                            }}
                        >
                            <Box mt={2}>
                                <Typography
                                    color="gold"
                                    fontWeight="bold"
                                    fontSize={{ xs: 15, sm: 20 }}
                                >
                                    {`${t("goldenegg.currentpoint")} ${currentPoints.toLocaleString()}`}
                                </Typography>
                                <Typography
                                    color="gold"
                                    fontWeight="bold"
                                    fontSize={{ xs: 15, sm: 20 }}
                                >
                                    {`${t("goldenegg.currentpoint")} ${process.env.REACT_APP_CURRENCY_CODE} ${currentCredit.toLocaleString()}`}
                                </Typography>

                            </Box>
                        </Box>

                        {/* Right Board - Reward Selector */}
                        <Box
                            sx={{
                                flex: 1.5,
                                position: "relative",
                                backgroundImage: mobileView ? `url(/assets/img/golden_egg/board/board_mobile.png)` : `url(/assets/img/golden_egg/board/board_long.png)`,
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "100% 100%", // make it stretch fully
                                backgroundPosition: "center",
                                minHeight: { xs: 150, sm: 180, md: 200 },
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                p: { xs: 2, sm: 3 },
                                color: "white",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "100%",
                                }}
                            >
                                {rewards.map((reward, index) => (
                                    <Button
                                        key={index}
                                        onClick={() => selectReward(index)}
                                        disabled={currentPoints < reward.points}
                                        sx={{
                                            flexDirection: "column",
                                            p: mobileView ? 1 : 1.5,
                                            border: selectedReward === index ? 4 : 2,
                                            borderRadius: 5,
                                            borderColor: selectedReward === index ? theme.palette.primary.main : "white",
                                            color: "white",
                                            textTransform: "none",
                                            position: "relative",
                                            transition: "transform 0.3s",
                                            backgroundColor: theme.palette.background.paper,
                                            transform: selectedReward === index ? "scale(1.2)" : "none",
                                            zIndex: selectedReward === index ? 1 : 0,
                                            "&:hover": {
                                                backgroundColor: theme.palette.background.paper
                                            },
                                            cursor: "none"
                                        }}
                                    >
                                        <img
                                            src={hammerImages[index]?.src}
                                            alt={hammerImages[index]?.alt}
                                            style={{ height: mobileView ? 40 : 60 }}
                                        />

                                        <Typography fontSize={{ xs: 12, sm: 16 }}>
                                            {`${t("goldenegg.spendpoint")} ${reward.label}`}
                                        </Typography>
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    {/* Egg grid */}
                    {selectedReward !== null && (
                        <Box
                        >
                            <Box display="flex" justifyContent="center">
                                <Box
                                    display={currentPoints >= rewards[selectedReward]?.points ? "grid" : "none"}
                                    gridTemplateColumns="repeat(3, 1fr)"
                                    gap={mobileView ? 2 : 4}
                                    justifyItems="center"
                                    mb={1}
                                >
                                    {eggs.map((egg, index) => (
                                        <Box
                                            key={index}
                                            onClick={!isClaiming ? crackEgg : undefined}
                                            sx={{
                                                width: { xs: 120, sm: 150 },
                                                height: { xs: 150, sm: 190 },
                                                borderRadius: "50%",
                                                color: "white",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "transform 0.2s",
                                                cursor: isClaiming ? "not-allowed" : "pointer",
                                                opacity: isClaiming ? 0.5 : 1,
                                                "&:hover": {
                                                    transform: "scale(1.2)",
                                                },
                                            }}
                                        >
                                            <Box
                                                component="div"
                                                sx={{
                                                    width: { xs: 120, sm: 150 },
                                                    height: { xs: 150, sm: 190 },
                                                    backgroundImage: `url(${eggImages[selectedReward ?? 0]})`,
                                                    backgroundRepeat: "no-repeat",
                                                    backgroundPosition: "center",
                                                    backgroundSize: "contain",
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Box
                        sx={{
                            position: "relative",
                            border: "5px solid gold",
                            bgcolor: "rgba(0,0,0,1)",
                            borderRadius: { xs: 3, md: 5 },
                            py: mobileView ? 1 : 3,
                            px: mobileView ? 2 : 3,
                            mb: 10,
                            mx: { xs: 1, sm: 5, md: 20 },
                            display: "flex",
                            flexDirection: "column",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                        }}
                    >
                        <Box
                            sx={{
                                color: "white",
                                fontSize: { xs: 12, md: 16 },
                                lineHeight: 1.8,
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                maxWidth: "100%",
                                "& table": {
                                    borderCollapse: "collapse",
                                    width: "100%",
                                },
                                "& th, & td": {
                                    borderStyle: "solid",
                                    borderWidth: 1,
                                    borderColor: "inherit",
                                    padding: "4px 8px",
                                },
                            }}
                            dangerouslySetInnerHTML={{ __html: goldenEggDesc?.result }}
                        />
                    </Box>
                </Box>
            </Box>
        </Box >
    );

}
